// Interview evaluation form (Phase 1c) — a time-boxed scorecard. Opened per
// interview; editable only within the window (10 min) then auto-locked.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Lock } from 'lucide-react';
import { PrimaryBtn, OutlineBtn, Pill, CenterSpinner } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';

const RECS = ['Advance', 'Hold', 'Reject'];

export default function InterviewEval({ interviewId, candName, onClose, showToast }) {
  const [data, setData] = useState(null);
  const [scores, setScores] = useState({});
  const [rec, setRec] = useState('');
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!interviewId) { setData(null); return; }
    setData(null);
    api.openEvaluation(interviewId).then((d) => {
      setData(d); setScores(d.scores || {}); setRec(d.recommendation || ''); setComments(d.comments || '');
    }).catch((e) => { showToast?.(e.message); onClose?.(); });
  }, [interviewId]);

  const overall = (() => {
    const vals = Object.values(scores).map(Number).filter((n) => n >= 1 && n <= 5);
    return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length / 5) * 100) : 0;
  })();

  const submit = async () => {
    setBusy(true);
    try { const u = await api.submitEvaluation(interviewId, { scores, recommendation: rec || null, comments }); setData(u); showToast?.('Evaluation submitted'); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  const locked = data?.locked;

  return (
    <Dialog open={!!interviewId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Evaluation · {candName || ''}</DialogTitle>
      <DialogContent>
        {!data ? <CenterSpinner /> : (
          <div style={{ paddingTop: 6 }}>
            {locked ? (
              <div className="flex items-center gap-2" style={{ background: 'rgba(240,68,56,.1)', color: AG.errorDark, borderRadius: 10, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                <Lock size={15} /> Locked — the {data.windowMinutes}-minute window has passed. Read-only.
              </div>
            ) : (
              <p style={{ fontSize: 12.5, color: AG.n500, marginBottom: 14 }}>Score each area 1–5. The form locks {data.windowMinutes} minutes after it was opened.</p>
            )}
            <div style={{ display: 'grid', gap: 10 }}>
              {data.criteria.map((cr) => (
                <div key={cr} className="flex items-center justify-between" style={{ gap: 10 }}>
                  <span style={{ fontSize: 13.5, color: AG.text, fontWeight: 500 }}>{cr}</span>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" disabled={locked} onClick={() => setScores({ ...scores, [cr]: n })}
                        style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${scores[cr] === n ? AG.primary : AG.n300}`, background: scores[cr] === n ? AG.primary : '#fff', color: scores[cr] === n ? '#fff' : AG.n600, fontWeight: 700, fontSize: 13, cursor: locked ? 'default' : 'pointer' }}>{n}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between" style={{ marginTop: 16 }}>
              <span style={{ fontSize: 13, color: AG.n600, fontWeight: 600 }}>Recommendation</span>
              <div className="flex items-center gap-2">
                {RECS.map((r) => (
                  <button key={r} type="button" disabled={locked} onClick={() => setRec(r)}
                    style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${rec === r ? AG.primary : AG.n300}`, background: rec === r ? AG.primaryLightest : '#fff', color: rec === r ? AG.primaryDark : AG.n600, fontWeight: 600, fontSize: 12.5, cursor: locked ? 'default' : 'pointer' }}>{r}</button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Comments</span>
              <TextField fullWidth size="small" multiline minRows={2} value={comments} onChange={(e) => setComments(e.target.value)} disabled={locked} sx={{ mt: 0.5 }} />
            </div>

            <div className="flex items-center justify-between" style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${AG.divider}` }}>
              <span style={{ fontSize: 13, color: AG.n500 }}>Overall score</span>
              <Pill tone={overall >= 70 ? 'success' : overall >= 40 ? 'warning' : 'neutral'}>{overall}%</Pill>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <OutlineBtn onClick={onClose}>Close</OutlineBtn>
        {data && !locked && <PrimaryBtn onClick={submit} disabled={busy}>Submit</PrimaryBtn>}
      </DialogActions>
    </Dialog>
  );
}
